import type { BridgeController, ProviderBridge } from "./types"

// Spotify Embed bridge.
//
// Why not the official IFrame API: `IFrameAPI.createController(el, …)` REPLACES
// the passed element with an iframe it constructs and owns. Our iframe is
// rendered by React inside `content-player`'s LiveIframe (which is do-not-
// modify), so letting Spotify swap the node out from under React desyncs the
// virtual DOM and crashes on unmount. Instead we speak the same postMessage
// protocol the official controller wraps, directly against the already-mounted
// iframe — exactly how the YouTube bridge drives its live iframe.
//
// Protocol (verified against the official iframe-api bundle's IframeCommands /
// IframeAPIEvent enums):
//   inbound  `{ type: "ready" }` then `{ type: "playback_update", payload: {
//              isPaused, isBuffering, position, duration } }`
//   outbound `{ command: "play" | "pause" | "toggle" | "load_complete_ack" }`
// The embed will not stream playback_update until the host acks its "ready"
// with `load_complete_ack` — the same handshake the official controller does
// (it flushes its command queue and acks on ready). There is no volume/mute
// command, so `setMuted` is a best-effort no-op (matching the pre-bridge
// behaviour — the chrome suppresses the mute toggle for Spotify).
//
// No test harness — verify manually by pressing play on a Spotify audio track
// in the immersive player and watching the open stage's record spin/freeze.

const READY_ACK = "load_complete_ack"

interface SpotifyPlaybackPayload {
  isPaused?: boolean
  position?: number
  duration?: number
}

interface SpotifyEmbedMessage {
  type?: string
  payload?: SpotifyPlaybackPayload
}

export const spotifyBridge: ProviderBridge = {
  matches: (url) => url.startsWith("https://open.spotify.com/embed/"),

  attach: (iframe) => {
    let destroyed = false
    // The embed only streams updates / accepts commands once we've acked its
    // "ready". Buffer anything issued before then so a mount-time call isn't
    // lost to the handshake race.
    let handshaken = false
    let ended = false
    let lastPlaying = false
    const endedListeners = new Set<() => void>()
    const playStateListeners = new Set<(playing: boolean) => void>()
    const pending: string[] = []

    // '*' target origin mirrors the official controller — the embed may serve
    // from more than one Spotify origin, and a mismatched explicit origin drops
    // the message silently.
    const post = (command: string) => {
      iframe.contentWindow?.postMessage({ command }, "*")
    }

    const flush = () => {
      if (!handshaken) return
      while (pending.length) post(pending.shift()!)
    }

    const enqueue = (command: string) => {
      if (handshaken) post(command)
      else pending.push(command)
    }

    const notifyPlaying = (playing: boolean) => {
      if (playing === lastPlaying) return
      lastPlaying = playing
      playStateListeners.forEach((cb) => cb(playing))
    }

    const handler = (event: MessageEvent) => {
      if (destroyed) return
      // Only trust messages from this embed's own frame.
      if (event.source !== iframe.contentWindow) return
      const data = event.data as SpotifyEmbedMessage | null
      if (!data || typeof data !== "object") return

      if (data.type === "ready" && !handshaken) {
        // Ack so the embed starts streaming playback_update, then flush any
        // commands issued before the handshake landed.
        handshaken = true
        post(READY_ACK)
        flush()
        return
      }

      if (data.type === "playback_update" && data.payload) {
        // Belt-and-braces: if the standalone "ready" was missed (attached a
        // beat late), the first update still unblocks the command queue.
        if (!handshaken) {
          handshaken = true
          flush()
        }
        const { isPaused, position, duration } = data.payload
        if (typeof isPaused === "boolean") notifyPlaying(!isPaused)
        if (
          !ended &&
          typeof position === "number" &&
          typeof duration === "number" &&
          duration > 0 &&
          position >= duration
        ) {
          ended = true
          endedListeners.forEach((cb) => cb())
        }
      }
    }

    if (typeof window !== "undefined") {
      window.addEventListener("message", handler)
    }

    const controller: BridgeController = {
      play: () => enqueue("play"),
      pause: () => enqueue("pause"),
      setMuted: () => {
        // No volume/mute command in the embed protocol — honoured as a no-op.
        // The chrome keeps its mute toggle suppressed for Spotify (see
        // hasMutableAudio) so nothing promises a mute we can't deliver.
      },
      subscribeEnded: (cb) => {
        endedListeners.add(cb)
        return () => {
          endedListeners.delete(cb)
        }
      },
      subscribePlayState: (cb) => {
        playStateListeners.add(cb)
        cb(lastPlaying)
        return () => {
          playStateListeners.delete(cb)
        }
      },
      destroy: () => {
        destroyed = true
        endedListeners.clear()
        playStateListeners.clear()
        pending.length = 0
        if (typeof window !== "undefined") {
          window.removeEventListener("message", handler)
        }
      },
    }

    return controller
  },
}
